import {Inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {PasswordResetKind} from "../password-reset-kind";

/**
 * The anonymous "Passwort vergessen" calls, for **both** credential stores (#279).
 *
 * Each flow has its own three endpoints, and this service routes between them on an explicit
 * {@link PasswordResetKind} rather than by trying one and falling back to the other. A fall-back would be the
 * silent disambiguation the split login exists to remove — and it would be worse here than at sign-in, because
 * the answer is observable to an anonymous caller: "which store holds this address?" would become a question you
 * can ask by timing two requests.
 *
 * Every request answers **200 whatever happened**, by design. A distinct answer for an address nobody uses would
 * turn either endpoint into a directory of who has an account here.
 */
@Injectable({
  providedIn: 'root'
})
export class PasswordHttpService {
  constructor(private httpClient: HttpClient, @Inject("BASE_URL") private baseUrl: string) {
  }

  /** Asks for a reset mail. Answers 200 whether or not the address has an account of that kind. */
  forgotPassword(email: string, kind: PasswordResetKind = 'legacy'): Observable<any> {
    return kind === 'account'
      ? this.httpClient.post(`${this.baseUrl}/auth/local/password/reset`, {email: email})
      : this.httpClient.post(`${this.baseUrl}/auth/user/resetpassword`, {email: email});
  }

  /** Whether a link is still good — drives "set a new password" vs. "this link has expired". */
  checkToken(token: string, uid: string, kind: PasswordResetKind = 'legacy'): Observable<any> {
    return kind === 'account'
      ? this.httpClient.post(`${this.baseUrl}/auth/local/password/reset/check`, {token: token, uid: uid})
      : this.httpClient.post(`${this.baseUrl}/auth/user/CheckResetToken`, {token: token, uid: uid});
  }

  /**
   * Spends the link and sets the new password.
   *
   * The native flow is a POST because that is the verb `LocalAuthController` declares; the legacy one has always
   * been a PUT. Each stays at its own controller's verb deliberately — a wrong verb on this branch answers
   * **405**, and the client half of that contract is pinned, so quietly "harmonising" the two would produce a
   * refusal that reads like a dead session.
   */
  resetPassword(token: string, uid: string, password: string,
                kind: PasswordResetKind = 'legacy'): Observable<any> {
    return kind === 'account'
      ? this.httpClient.post(`${this.baseUrl}/auth/local/password/reset/complete`,
        {token: token, uid: uid, password: password})
      : this.httpClient.put(`${this.baseUrl}/auth/user/resetpasswordtoken`,
        {token: token, uid: uid, password: password});
  }
}
